"use client";

import { useRef, useState } from "react";
import { FileDown } from "lucide-react";
import type { ItinerarySuccess } from "@/lib/planner/api-types";
import type { PlannerPreferences } from "@/lib/planner/preferences";
import { routeNotesHtml } from "@/lib/planner/saved-route";

export function RouteDownload({ slug, plan, preferences }: {
  slug: string;
  plan: ItinerarySuccess;
  preferences: PlannerPreferences;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const print = () => {
    try {
      if (!frame.current?.contentWindow) throw new Error("Preview unavailable");
      frame.current.contentWindow.focus();
      frame.current.contentWindow.print();
      setMessage("Choose Save as PDF in the print window to keep your route on your phone.");
    } catch { setMessage("Printing is unavailable in this browser. Download the route notes below and use your browser’s Print or Share option to save a PDF."); }
  };
  const prepare = () => {
    setMessage(null);
    const html = routeNotesHtml({ version: 1, slug, plan, preferences, savedAt: new Date().toISOString(), status: "saved", progress: 0 });
    if (html === preview) print();
    else setPreview(html);
  };
  if (!plan.itinerary.legs.length) return null;
  return <section className="mb-4 rounded-2xl border border-teal/25 bg-teal/5 p-4 md:p-5" aria-label="Download your route" data-export-ignore>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3"><FileDown aria-hidden size={25} className="mt-1 shrink-0 text-teal" /><div>
        <h2 className="font-heading text-lg font-bold text-navy">Take your day with you</h2>
        <p className="mt-1 max-w-xl text-sm leading-relaxed text-text-body">Your route map, best and hardest runs, distances, trade-offs and every step in one PDF.</p>
      </div></div>
      <button type="button" onClick={prepare} className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-xl bg-teal px-5 py-3 text-sm font-bold text-white hover:bg-teal-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"><FileDown size={18} aria-hidden />Save route as PDF</button>
    </div>
    <p className="mt-2 text-xs text-text-muted">Opens a print preview. Choose Save as PDF to download.</p>
    {message && <p role="status" className="mt-3 text-sm text-navy">{message}</p>}
    {preview && <div className="mt-4"><button type="button" className="mb-2 min-h-[44px] text-sm font-semibold text-navy underline" onClick={() => setPreview(null)}>Close PDF preview</button><iframe ref={frame} title="Route PDF preview" sandbox="allow-same-origin allow-modals" srcDoc={preview} onLoad={print} className="h-[480px] w-full rounded-xl border border-border bg-white" /></div>}
  </section>;
}
